import { useState } from 'react';
import { ShieldCheck, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth.ts';
import { useUsers } from '@/hooks/useUsers.ts';
import {
  useApprovalPolicies,
  useCreateApprovalPolicy,
  useDeleteApprovalPolicy,
  type PolicyApprover,
} from '@/hooks/useApprovalPolicies.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Switch } from '@/components/ui/switch.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';

const CHANGE_TYPES = ['standard', 'normal', 'emergency'];
const RISK_LEVELS = ['low', 'medium', 'high'];

/**
 * Approval policies: admin rules that, when a submitted change matches, materialize
 * the approvals it requires. v1 UI supports one stage with a quorum; multi-stage and
 * device-tag matching are available via the API.
 */
export function ApprovalPoliciesSection() {
  const { user } = useAuth();
  const orgId = user?.organizationId ?? user?.activeContext?.organizationId ?? null;
  const { data: policies } = useApprovalPolicies();
  const { data: users } = useUsers(orgId);
  const create = useCreateApprovalPolicy();
  const remove = useDeleteApprovalPolicy();

  const [name, setName] = useState('');
  const [changeTypes, setChangeTypes] = useState<string[]>([]);
  const [riskLevels, setRiskLevels] = useState<string[]>([]);
  const [minDevices, setMinDevices] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [quorumKind, setQuorumKind] = useState<'all' | 'any' | 'n_of'>('all');
  const [quorumN, setQuorumN] = useState('');
  const [approverIds, setApproverIds] = useState<string[]>([]);

  const toggle = (list: string[], setList: (v: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const canSubmit = name.trim() !== '' && (autoApprove || approverIds.length > 0) && !create.isPending;

  const submit = () => {
    if (!canSubmit) return;
    const approvers: PolicyApprover[] = approverIds.map((id) => ({ approverType: 'user', approverId: id }));
    create.mutate(
      {
        name: name.trim(),
        changeTypes: changeTypes.length ? changeTypes : undefined,
        riskLevels: riskLevels.length ? riskLevels : undefined,
        minDeviceCount: minDevices ? Number(minDevices) : undefined,
        autoApprove,
        // Auto-approve policies need no approvers/stages.
        stages: autoApprove
          ? []
          : [{ name: 'Review', quorumKind, quorumN: quorumKind === 'n_of' ? Number(quorumN) || 1 : undefined, approvers }],
      },
      {
        onSuccess: () => {
          setName('');
          setChangeTypes([]);
          setRiskLevels([]);
          setMinDevices('');
          setAutoApprove(false);
          setQuorumKind('all');
          setQuorumN('');
          setApproverIds([]);
        },
      },
    );
  };

  const userName = (id: string) => {
    const u = users?.find((x) => x.id === id);
    return u ? `${u.firstName} ${u.lastName}`.trim() || u.email : id;
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Approval policies</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        When a submitted change matches a policy, it requires that policy's approvals. The
        highest-priority match wins; with no match, the change uses its manually-added approvers.
      </p>

      {/* Existing policies */}
      {!policies || policies.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-4">No approval policies yet.</p>
      ) : (
        <ul className="space-y-2 mb-4">
          {policies.map((p) => (
            <li key={p.id} className="flex items-start justify-between gap-3 rounded-md border border-border-subtle px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{p.name}</span>
                  {p.autoApprove ? <Badge variant="secondary">Auto-approve</Badge> : null}
                  {!p.enabled ? <Badge variant="outline">Disabled</Badge> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.changeTypes.length ? `types: ${p.changeTypes.join(', ')}` : 'any type'}
                  {p.riskLevels.length ? ` · risk: ${p.riskLevels.join(', ')}` : ''}
                  {p.minDeviceCount ? ` · ≥${String(p.minDeviceCount)} devices` : ''}
                  {p.stages.length
                    ? ` · ${String(p.stages.length)} stage${p.stages.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => { remove.mutate(p.id); }}
                disabled={remove.isPending}
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Create form */}
      <div className="rounded-md border border-border-subtle p-3 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="pol-name">Name</Label>
          <Input id="pol-name" value={name} onChange={(e) => { setName(e.target.value); }} placeholder="e.g. High-risk needs CAB" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Matches change types</Label>
            <div className="flex flex-wrap gap-3">
              {CHANGE_TYPES.map((ct) => (
                <label key={ct} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer">
                  <Checkbox checked={changeTypes.includes(ct)} onCheckedChange={() => { toggle(changeTypes, setChangeTypes, ct); }} />
                  {ct}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Matches risk levels</Label>
            <div className="flex flex-wrap gap-3">
              {RISK_LEVELS.map((rl) => (
                <label key={rl} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer">
                  <Checkbox checked={riskLevels.includes(rl)} onCheckedChange={() => { toggle(riskLevels, setRiskLevels, rl); }} />
                  {rl}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pol-min">Min devices</Label>
            <Input
              id="pol-min"
              type="number"
              min={0}
              className="w-28"
              value={minDevices}
              onChange={(e) => { setMinDevices(e.target.value); }}
              placeholder="any"
            />
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch id="pol-auto" checked={autoApprove} onCheckedChange={setAutoApprove} />
            <Label htmlFor="pol-auto" className="font-normal">Auto-approve (no approval needed)</Label>
          </div>
        </div>

        {!autoApprove ? (
          <div className="space-y-3 rounded-md bg-muted/40 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approval stage</div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label>Quorum</Label>
                <Select value={quorumKind} onValueChange={(v) => { setQuorumKind(v as 'all' | 'any' | 'n_of'); }}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All approvers</SelectItem>
                    <SelectItem value="any">Any one</SelectItem>
                    <SelectItem value="n_of">N of them</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {quorumKind === 'n_of' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="pol-n">N</Label>
                  <Input id="pol-n" type="number" min={1} className="w-20" value={quorumN} onChange={(e) => { setQuorumN(e.target.value); }} />
                </div>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Approvers</Label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {(users ?? []).map((u) => (
                  <label key={u.id} className="flex items-center gap-1.5 text-sm cursor-pointer rounded border border-border-subtle px-2 py-1">
                    <Checkbox
                      checked={approverIds.includes(u.id)}
                      onCheckedChange={() => { toggle(approverIds, setApproverIds, u.id); }}
                    />
                    {userName(u.id)}
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          {create.isError && <span className="text-xs text-destructive">Failed to create policy.</span>}
          <Button size="sm" onClick={submit} disabled={!canSubmit}>
            {create.isPending ? 'Creating…' : 'Add policy'}
          </Button>
        </div>
      </div>
    </div>
  );
}
